import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { v4 as uuidv4 } from 'uuid';
import type { QuestionContent, FillBlankContent, FillBlankAnswer } from '@/types';
import styles from './QuestionEditor.module.scss';

interface FillBlankEditorProps {
  content: QuestionContent | null;
  questionText: string;
  onChange: (content: QuestionContent) => void;
}

export function FillBlankEditor({ content, questionText, onChange }: FillBlankEditorProps) {
  const [blanks, setBlanks] = useState<FillBlankAnswer[]>([]);
  const [caseSensitive, setCaseSensitive] = useState(false);
  const [partialCredit, setPartialCredit] = useState(false);

  // Count {{blank}} placeholders in question text
  const blankCount = (questionText.match(/\{\{blank\}\}/g) || []).length;

  // Initialize blanks based on question text
  useEffect(() => {
    if (content && content.type === 'fill_blank') {
      setBlanks(content.blanks);
      setCaseSensitive(content.caseSensitive || false);
      setPartialCredit(content.partialCredit || false);
    } else if (blankCount > 0) {
      // Create blanks array matching the number of {{blank}} in question text
      const newBlanks: FillBlankAnswer[] = Array.from({ length: blankCount }, (_, i) => ({
        id: uuidv4(),
        position: i,
        acceptedAnswers: [''],
        placeholder: '',
      }));
      setBlanks(newBlanks);
    }
  }, [content, blankCount]);

  // Update parent when blanks change
  useEffect(() => {
    if (blanks.length > 0 && blanks.every((blank) => blank.acceptedAnswers.some((ans) => ans.trim() !== ''))) {
      const newContent: FillBlankContent = {
        type: 'fill_blank',
        blanks,
        caseSensitive,
        partialCredit,
      };
      onChange(newContent);
    }
  }, [blanks, caseSensitive, partialCredit, onChange]);

  const handleAcceptedAnswersChange = (position: number, value: string) => {
    setBlanks((prev) =>
      prev.map((blank) =>
        blank.position === position
          ? { ...blank, acceptedAnswers: value.split(',').map((ans) => ans.trim()).filter(Boolean) }
          : blank
      )
    );
  };

  const handlePlaceholderChange = (position: number, value: string) => {
    setBlanks((prev) =>
      prev.map((blank) =>
        blank.position === position ? { ...blank, placeholder: value } : blank
      )
    );
  };

  return (
    <div className={styles.editor}>
      <h4 className={styles.editor__heading}>Fill in the Blank Configuration</h4>

      <div className={styles.editor__info}>
        <strong>Instructions:</strong> Use <code>{'{{blank}}'}</code> in your question text to mark where blanks should appear.
        Found {blankCount} blank(s) in your question.
      </div>

      {blankCount === 0 ? (
        <p className={styles.editor__warning}>
          ⚠️ No blanks detected. Add <code>{'{{blank}}'}</code> to your question text above.
        </p>
      ) : (
        <>
          <div className={styles.editor__field}>
            <label className={styles.editor__checkboxLabel}>
              <input
                type="checkbox"
                className={styles.editor__checkbox}
                checked={caseSensitive}
                onChange={(e) => setCaseSensitive(e.target.checked)}
              />
              <span className={styles.editor__checkboxText}>
                Case sensitive matching
              </span>
            </label>
          </div>

          <div className={styles.editor__field}>
            <label className={styles.editor__checkboxLabel}>
              <input
                type="checkbox"
                className={styles.editor__checkbox}
                checked={partialCredit}
                onChange={(e) => setPartialCredit(e.target.checked)}
              />
              <span className={styles.editor__checkboxText}>
                Award partial credit for some correct answers
              </span>
            </label>
          </div>

          <div className={styles.editor__optionsList}>
            <AnimatePresence>
              {blanks.map((blank, index) => (
                <motion.div
                  key={blank.id}
                  className={styles.editor__option}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  layout
                >
                  <div className={styles.editor__optionHeader}>
                    <span className={styles.editor__optionNumber}>{index + 1}</span>
                    <h5 className={styles.editor__label}>Blank {index + 1}</h5>
                  </div>

                  <div className={styles.editor__field}>
                    <label className={styles.editor__label}>
                      Accepted Answers (comma-separated) *
                    </label>
                    <input
                      type="text"
                      className={styles.editor__input}
                      value={blank.acceptedAnswers.join(', ')}
                      onChange={(e) => handleAcceptedAnswersChange(blank.position, e.target.value)}
                      placeholder="e.g., answer1, answer2, answer3"
                    />
                  </div>

                  <div className={styles.editor__field}>
                    <label className={styles.editor__label}>
                      Placeholder (optional)
                    </label>
                    <input
                      type="text"
                      className={styles.editor__input}
                      value={blank.placeholder || ''}
                      onChange={(e) => handlePlaceholderChange(blank.position, e.target.value)}
                      placeholder="e.g., Enter a number..."
                      maxLength={50}
                    />
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        </>
      )}
    </div>
  );
}
